import { countryName, isVisible, resolveText, type FieldSpec, type FormDoc, type LetterDoc, type UploadDoc } from "@gsignatures/core";
import { useState } from "react";
import { ActionBar } from "../components/ActionBar";
import { Icon } from "../components/Icon";
import { isoDayLong, shortHash } from "../lib/format";
import { useFocusHeading } from "../lib/hooks";
import type { Answers } from "../state/answers";
import type { FlowCtx } from "../state/flow";
import { S } from "../strings";

interface Props {
  ctx: FlowCtx;
  letters: LetterDoc[];
  /** Pictures added (an ID proof). */
  uploads?: UploadDoc[];
  form: FormDoc | undefined;
  answers: Answers | null;
  onEdit: (fieldId: string) => void;
}

function display(spec: FieldSpec, v: unknown): string {
  if (spec.type === "toggle") return v === true || (v === undefined && spec.defaultValue === true) ? S.review.yes : S.review.no;
  const s = typeof v === "string" ? v.trim() : "";
  if (!s) return S.review.blank;
  if (spec.type === "country") return countryName(s);
  if (spec.type === "select") return spec.options?.find((o) => o.value === s)?.label ?? s;
  return s;
}

export function ReviewScreen({ ctx, letters, form, uploads = [], answers, onEdit }: Props) {
  const { session, w, api } = ctx;
  const heading = useFocusHeading();
  const [busy, setBusy] = useState(false);

  async function next() {
    if (busy) return;
    if (answers) {
      setBusy(true);
      const ok = await answers.flush();
      setBusy(false);
      if (!ok) return;
    }
    if (session.step.step === "review") {
      setBusy(true);
      const s = await ctx.run(() => api.review());
      setBusy(false);
      if (s) ctx.apply(s);
      return;
    }
    if (session.step.step === "form" || session.step.step === "chooseForm") {
      ctx.go("tax");
      return;
    }
    ctx.go("sign");
  }

  const pack = answers?.pack;
  const warnings = answers ? answers.validation.issues.filter((i) => i.severity === "warning") : [];
  const termsOf = (l: LetterDoc) => l.terms.slice(0, 3).map((t) => t.value).join(" · ");

  return (
    <>
      <main className="page" id="main">
        <div className="panel panel-pad narrow-panel">
          <div className="stack">
            <div>
              <div className="eyebrow">{S.review.eyebrow}</div>
              <h1 className="title" ref={heading} tabIndex={-1}>
                {S.review.title}
              </h1>
              <p className="lede">{S.review.lede}</p>
            </div>
            <ul className="doc-list">
              {letters.map((letter) => (
                <li className="doc-row" key={letter.id}>
                  <div className="thumb" aria-hidden="true" />
                  <div>
                    <b>{letter.title}</b> <span className="faint">· {letter.version} · {S.review.pages(letter.pages)}</span>
                    <div className="muted small">{termsOf(letter)}</div>
                  </div>
                  <button type="button" className="btn-link" onClick={() => ctx.viewLetter(letter.id)} aria-label={`${S.review.view} ${letter.title}`}>
                    {S.review.view}
                  </button>
                </li>
              ))}
              {form && pack && (
                <li className="doc-row">
                  <div className="thumb form" aria-hidden="true" />
                  <div>
                    <b>{pack.title}</b>{" "}
                    <span className="faint">
                      · {pack.issuer}, {S.tax.revision(pack.revision)}
                    </span>
                    <div className="muted small">{S.review.validThrough(isoDayLong(pack.validUntil(new Date())))}</div>
                  </div>
                  <button type="button" className="btn-link" onClick={() => ctx.go("tax")} aria-label={`${S.review.view} ${pack.title}`}>
                    {S.review.view}
                  </button>
                </li>
              )}
              {uploads.map((u) => (
                <li className="doc-row" key={u.id}>
                  <div className="thumb" aria-hidden="true" />
                  <div>
                    <b>{u.title}</b> <span className="faint">· {S.upload.count(u.files.length, u.maxFiles)}</span>
                  </div>
                  <button type="button" className="btn-link" onClick={() => ctx.go("upload")} aria-label={`${S.review.view} ${u.title}`}>
                    {S.review.view}
                  </button>
                </li>
              ))}
            </ul>
            {warnings.map((wn) => (
              <div className="note flag" key={`${wn.field}-${wn.rule}`}>
                <Icon n="info" />
                <span>
                  {wn.message} {S.review.warningTail(w)}
                </span>
              </div>
            ))}
            {answers &&
              pack &&
              pack.sections.map((sec) => {
                const specs = sec.fields
                  .map((id) => pack.fields.find((f) => f.id === id))
                  .filter((f): f is FieldSpec => !!f && isVisible(f, answers.values));
                if (specs.length === 0) return null;
                return (
                  <section key={sec.id} className="answer-group" aria-labelledby={`rv-${sec.id}`}>
                    <h2 className="answer-group-title" id={`rv-${sec.id}`}>
                      {sec.title}
                    </h2>
                    <dl className="answers">
                      {specs.map((f) => {
                        const label = resolveText(f.label, answers.values);
                        const v = display(f, answers.values[f.id]);
                        const blank = v === S.review.blank;
                        return (
                          <div className="answer" key={f.id}>
                            <div>
                              <dt>{label}</dt>
                              <dd className={blank ? "faint" : f.sensitive || f.type === "date" ? "mono" : undefined}>{v}</dd>
                            </div>
                            <button type="button" className="btn-link" onClick={() => onEdit(f.id)} aria-label={S.review.editLabel(label)}>
                              {S.review.edit}
                            </button>
                          </div>
                        );
                      })}
                    </dl>
                  </section>
                );
              })}
            {letters.map((letter) => (
              <div className="note" key={letter.id}>
                <Icon n="fingerprint" />
                <span>
                  {S.review.fingerprint} {letter.title} <span className="mono">{shortHash(letter.sha256)}</span>
                </span>
              </div>
            ))}
            <div className="row between desk-actions">
              <button type="button" className="btn btn-quiet" onClick={() => ctx.go("tax")}>
                <Icon n="back" /> {S.common.back}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => void next()} disabled={busy} aria-busy={busy}>
                {S.review.continue} <Icon n="chev" />
              </button>
            </div>
          </div>
        </div>
      </main>
      <ActionBar>
        <button type="button" className="btn btn-quiet" onClick={() => ctx.go("tax")}>
          {S.common.back}
        </button>
        <button type="button" className="btn btn-primary" onClick={() => void next()} disabled={busy}>
          {S.review.continue}
        </button>
      </ActionBar>
    </>
  );
}
